import React, { useEffect } from 'react';
import { Typography, Row, Col, Table } from 'antd';
import './Help.css';

const { Title, Text, Link } = Typography;

const Help: React.FC = () => {
  useEffect(() => {
    document.title = 'EHE Tracker - Help';
  }, []);

  // #region Data and Column Definitions For Submit Event Table

  const submitEventTableData = [
    {
      key: 'submitEventDate',
      section: 'Event Date',
      notes: `The date the event took place; if listing participation in a conference
              or other multi-day event, list the actual date of involvement, not the
              conference/event start day or a span of dates for a multi-day conference.`,
    },
    {
      key: 'submitEventLocation',
      section: 'Event Location',
      notes: `All events with formats coded as conference call or webinar should have
              locations coded as "Virtual/Other"; additionally, any media/interview
              events without a physical location/component should be coded as
              "Virtual/Other" (like a podcast).`,
    },
    {
      key: 'submitEventDescription',
      section: 'Event Description',
      notes: `Description of event and agency participation; any additional details
              that are not captured by subsequent sections should be included here. Note
              that if an event you are entering in the tracker is still "tentative" please
              indicate that here.`,
    },
    {
      key: 'submitFormat',
      section: 'Format',
      notes:
        'If desired format is not listed, select closest option from drop-down.',
    },
    {
      key: 'submitAudience(s)',
      section: 'Audience(s)',
      notes: `The event's audiences; see below for more information`,
    },
    {
      key: 'submitParticipatingAgencies',
      section: 'Participating Agencies',
      notes: 'All agencies who participated in the event.',
    },
    {
      key: 'submitAttendees&Speakers',
      section: 'Attendees & Speakers',
      notes: 'List key attendees',
    },
    {
      key: 'submitHHSPrincipal(s)',
      section: 'HHS Principal(s)',
      notes: 'List HSS principals attending or speaking at the event.',
    },
    {
      key: 'submitAdditionalHHSStaff',
      section: 'Additional HHS Staff',
      notes: 'List additional HHS staff attending or speaking at the event.',
    },
    {
      key: 'submitAdditionalAttendees',
      section: 'Additional Federal/Nonfederal Attendees',
      notes: `List additional noteworthy attendees. Please include Name, Role/Title
              and agency/office affiliation, as appropriate (e.g. John Doe, Program
              Manager, OASH/OIDP). Do not include Members of Congress here.`,
    },
    {
      key: 'submitMembersOfCongress',
      section: 'Members of Congress Attending',
      notes: `List each participating Member of Congress; if speaking to Congressional
              staff, list corresponding Member of Congress here, and note that it was
              staff in the event description.`,
    },
    {
      key: 'submitEHEJurisdictionVisit',
      section: 'EHE Jurisdiction Visit',
      notes: `Restricted to Yes/No; if yes, fill out the following corresponding
              target jurisdiction section. Yes is limited to physical travel (e.g. 
              not a phone call with a representative or congressional briefing with a
              representative of a target jurisdiction) to one of the 57 EHE
              jurisdictions. Only mark "yes" if the event is directly related to the
              EHE initiative.`,
    },
    {
      key: 'submitEHETargetJurisdiction:State(s)',
      section: 'EHE Target Jurisdiction: State(s)',
      notes: `If this event was a physical jurisdiction visit, ensure you have
              selected "yes" in the preceding question. If this event was not a
              physical jurisdiction visit but directly involved local jurisdiction
              stakeholders, please mark the corresponding jurisdiction here.`,
    },
    {
      key: 'submitEHETargetJurisdiction:Counties',
      section: 'EHE Target Jurisdiction: Counties',
      notes: `If this event was a physical jurisdiction visit, then note each represented
              target county. If this event was not a physical jurisdiction visit but
              directly involved local jurisdiction stakeholders, please mark the
              corresponding jurisdiction here.`,
    },
    {
      key: 'submitOtherLocalitiesReached',
      section: 'Other Localities Reached',
      notes: `Please list any specific non-EHE jurisdictions engaged in this event. For 
              example, if you met with the New Mexico HIV Community Planning and Action 
              Group, then please list “New Mexico”. Multiple locations should be separated 
              by semicolon (i.e. New Mexico; Boulder, CO; Fairfax, VA).`,
    },
    {
      key: 'submitReady,Set,PrEP',
      section: 'Ready, Set, PrEP',
      notes:
        'Select yes if this event discussed the Ready, Set, PrEP initiative.',
    },
  ];

  const submitEventTableColumns = [
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'submitSection',
      width: '25%',
      render: (text: any, row: any, index: any) => {
        if (index >= 7 && index <= 9) {
          return <Row className="Help-submit-table-indent">{text}</Row>;
        }
        return text;
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'submitNotes',
      render: (text: any, row: any, index: any) => {
        if (index === 2) {
          return (
            <Text>
              Description of event and agency participation; any additional
              details that are not captured by subsequent sections should be
              included here. Note that if an event you are entering in the
              tracker is still <Text strong>&quot;tentative&quot;</Text> please
              indicate that here.
            </Text>
          );
        }
        if (index === 11) {
          return (
            <Text>
              Restricted to Yes/No; if <Text strong>yes</Text>, fill out the
              following corresponding target jurisdiction section.{' '}
              <Text strong>Yes</Text> is limited to physical travel (e.g. not a
              phone call with a representative or congressional briefing with a
              representative of a target jurisdiction) to one of the 57 EHE
              jurisdictions. <Text strong>Only</Text> mark &quot;yes&quot; if
              the event is directly related to the EHE initiative.
            </Text>
          );
        }
        if (index === 15) {
          return (
            <Text>
              Select <Text strong>yes</Text> if this event discussed the Ready,
              Set, PrEP initiative.
            </Text>
          );
        }
        return text;
      },
    },
  ];

  // #endregion

  // #region Data and Column Definitions For Pre-Defined Values Table

  const preDefinedTableData = [
    {
      key: 'preDefinedAdvocacyGroups',
      preDefinedValues: 'Advocacy Groups',
      definitions: `An organization which advocates for and tries to advance the
                    interests of people living with or at risk of HIV (e.g. Black AIDS
                    Institute, NASTAD, AIDS United, Treatment Action Group).`,
    },
    {
      key: 'preDefinedCongress',
      preDefinedValues: 'Congress',
      definitions: 'Members of Congress and/or Congressional staffers.',
    },
    {
      key: 'preDefinedFederalAgencyStaff',
      preDefinedValues: 'Federal Agency Staff',
      definitions:
        'Federal agency staff and/or contractors; distinct from Congress.',
    },
    {
      key: 'preDefinedGlobal/International',
      preDefinedValues: 'Global/International',
      definitions: 'Non-U.S. partners/stakeholders (often a catch-all).',
    },
    {
      key: 'preDefinedHIVCommunityStakeholders',
      preDefinedValues: 'HIV Community Stakeholders',
      definitions: `Members of the public, or from relevant community groups, with
                    a vested interest in HIV, including people living with HIV.`,
    },
    {
      key: 'preDefinedFederalGrantees',
      preDefinedValues: 'Federal Grantees',
      definitions: 'Programs/recipients receiving federal funding.',
    },
    {
      key: 'preDefinedMedia',
      preDefinedValues: 'Media',
      definitions: `Press/news outlets (e.g. journalists, news station reporters,
                    radio hosts, etc.)`,
    },
    {
      key: 'preDefinedNon-HIVStakeholders',
      preDefinedValues: 'Non-HIV Stakeholders',
      definitions: `Organizations, associations, or other groups/entities whose
                    primary focus is not HIV (ex. pharmaceutical associations,
                    medical groups, health care providers; ex. not NASTAD).`,
    },
    {
      key: 'preDefinedResearchers',
      preDefinedValues: 'Researchers',
      definitions: 'Researchers and members of the academic community.',
    },
    {
      key: 'preDefinedState/LocalHealthDepts',
      preDefinedValues: 'State/Local Health Depts',
      definitions: 'State/Local health department representatives.',
    },
    {
      key: 'preDefinedState/LocalGovernment',
      preDefinedValues: 'State/Local Government',
      definitions: `State/Local government representatives, other than health
                    department.`,
    },
    {
      key: 'preDefinedTribal',
      preDefinedValues: 'Tribal',
      definitions: `Tribal leaders, tribal epidemiology centers, tribal community
                    groups, urban Indian partners, IHS clinicians.`,
    },
    {
      key: 'preDefinedWhiteHouse',
      preDefinedValues: 'White House',
      definitions: 'President or White House staff.',
    },
    {
      key: 'preDefinedProgrammaticSiteVisit',
      preDefinedValues: 'Programmatic Site Visit',
      definitions: `Either a programmatic visit of a grantee, OR a scheduled visit to
                    learn about a specific local program. (This is narrower than a
                    jurisdiction visit).`,
    },
    {
      key: 'preDefinedListeningSession',
      preDefinedValues: 'Listening Session',
      definitions: `A scheduled forum where stakeholders provide comments,
                    recommendations, and ideas on EHE to HHS staff. If an associated URL
                    exists, please paste it in the event description.`,
    },
    {
      key: 'preDefinedWebinar',
      preDefinedValues: 'Webinar',
      definitions: 'An online, educational presentation.',
    },
    {
      key: 'preDefinedStandardMeeting/Conference',
      preDefinedValues: 'Standard Meeting/Conference',
      definitions: `An established meeting or conference in which a federal agency is an
                    invited participant or has a formal speaking role. This meeting/conference
                    was not formed/established solely to discuss EHE, but is rather part
                    of a larger event, or an ongoing series of events. (e.g. a PACHA
                    meeting, USCA, CDC's National HIV Prevention Conference, Ryan White
                    conference).`,
    },
    {
      key: 'preDefinedConferenceCall',
      preDefinedValues: 'Conference Call',
      definitions: 'A closed-invite, virtual, interactive meeting.',
    },
    {
      key: 'preDefinedIn-PersonMeeting',
      preDefinedValues: 'In-Person Meeting',
      definitions: `A one-time scheduled dialogue with an agency representative for the
                    purpose of general information exchange (and is not a press interview
                    or briefing as defined below).`,
    },
    {
      key: 'preDefinedBriefing',
      preDefinedValues: 'Briefing',
      definitions: `A scheduled reporting on the progress/success of EHE, particularly to
                    Congress.`,
    },
    {
      key: 'preDefinedMediaInterview/Event',
      preDefinedValues: 'Media Interview/Event',
      definitions: `A conversation between a broadcast, print or online journalist and an
                    agency representative (also includes podcast/radio/video, satellite
                    media tour).`,
    },
    {
      key: 'preDefinedNewRelease',
      preDefinedValues: 'News Release',
      definitions: `A statement, announcement, or alert issued by an agency to media
                    outlets and journalists; does not include syndicated media coverage.`,
    },
    {
      key: 'preDefinedYes',
      preDefinedValues: 'Yes',
      definitions: `Event was either held in target jurisdiction, or was a meeting with
                    direct representation of target jurisdiction; If this is marked "Yes",
                    then "Target Jurisdiction--State" should be filled out, as should
                    "Target Jurisdiction--County" (if applicable).`,
    },
    {
      key: 'preDefinedNo',
      preDefinedValues: 'No',
      definitions: 'An event that does not fit the description above.',
    },
  ];

  const preDefinedTableColumns = [
    {
      title: 'Section',
      key: 'preDefinedSection',
      render: (text: any, row: any, index: any) => {
        if (index === 0) {
          return {
            children: <Text strong>Audience(s)</Text>,
            props: {
              rowSpan: 13,
            },
          };
        }
        if (index === 13) {
          return {
            children: <Text strong>Format</Text>,
            props: {
              rowSpan: 9,
            },
          };
        }
        if (index === 22) {
          return {
            children: <Text strong>EHE Jurisdiction Visit</Text>,
            props: {
              rowSpan: 2,
            },
          };
        }
        if (
          (index > 0 && index <= 12) ||
          (index > 13 && index <= 21) ||
          (index > 22 && index <= 23)
        ) {
          return {
            props: {
              rowSpan: 0,
            },
          };
        }
        return text;
      },
    },
    {
      title: 'Pre-Defined Values',
      key: 'preDefinedValues',
      dataIndex: 'preDefinedValues',
      width: '25%',
    },
    {
      title: 'Definitions/Implications',
      key: 'preDefinedDefinitions/Implications',
      dataIndex: 'definitions',
    },
  ];

  // #endregion

  // #region Render Section

  return (
    <Row>
      <Col span={14}>
        <Title>Help: Event Tracker Guide</Title>
        <Text className="Help-text">
          Thank you for your efforts to track federal engagements in support of{' '}
        </Text>
        <Text className="Help-text Help-text-italic">
          Ending the HIV Epidemic: A Plan for America (EHE).{' '}
        </Text>
        <Text className="Help-text">
          The Event Tracker captures the U.S. Department of Health & Human
          Services (HHS) agencies&apos; participation in events related to EHE.
        </Text>
        <Row className="Help-section-margin">
          <Text className="Help-text">
            Contributions are reviewed weekly by Oskian Kouzouian (
            <Link href="mailto:Oskian.Kouzouian@hhs.gov">
              Oskian.Kouzouian@hhs.gov
            </Link>
            ) and Lauren Rowson (
            <Link href="mailto:Lauren.Rowson@icfnext.com">
              Lauren.Rowson@icfnext.com
            </Link>
            ), typically on Friday mornings.
          </Text>
        </Row>
        <Row className="Help-section-margin">
          <Text className="Help-text">
            Please contact Oskian if you have any questions.
          </Text>
        </Row>
        <Row className="Help-section-margin">
          <Title level={3}>Submitting an Event</Title>
        </Row>
        <Text className="Help-text">
          On the Submit Event tab, we ask that you provide several key pieces of
          information about events in support of EHE that your agency is
          planning or participating.
        </Text>
        <Row className="Help-section-margin">
          <Text className="Help-text">
            Below, we list the data categories and notes about what information
            should be captured.
          </Text>
        </Row>
        <Row className="Help-section-margin">
          <Table
            dataSource={submitEventTableData}
            columns={submitEventTableColumns}
            pagination={false}
          />
        </Row>
        <Row className="Help-section-margin">
          <Title level={3}>Pre-Defined Values</Title>
        </Row>
        <Text className="Help-text">
          Several of the sections include pre-defined values to ensure
          consistent codification of events in the tracker.
        </Text>
        <Row className="Help-section-margin">
          <Text className="Help-text">
            Please follow the below conventions and definitions when coding
            event details. If no option exists to accurately code the
            information, choose the option that fits most closely and add
            clarifying information in the event description.
          </Text>
        </Row>
        <Row className="Help-section-margin">
          <Table
            className="Help-pre-defined-table"
            dataSource={preDefinedTableData}
            columns={preDefinedTableColumns}
            pagination={false}
          />
        </Row>
        <Row className="Help-section-margin">
          <Title level={3}>View My Event</Title>
        </Row>
        <Text className="Help-text">
          To view events that you have created or contributed to, select Filter
          and check Filter Events: Only My Events in View All Events.
        </Text>
        <Row className="Help-section-margin">
          <Title level={3}>Quality Assurance</Title>
        </Row>
        <Text className="Help-text">
          Each week after receiving all agency submissions, OIDP will review all
          updates as part of our quality assurance efforts. We may reach out to
          you if any clarifications are needed or if any information is missing.
        </Text>
        <Row className="Help-section-margin">
          <Title level={3}>Additional Assistance</Title>
        </Row>
        <Text className="Help-text">
          Oskian and Lauren are always available by email for any questions,
          concerns, or suggestions you might have.
        </Text>
      </Col>
    </Row>
  );

  // #endregion
};

export default Help;
